import * as Popover from "@radix-ui/react-popover";
import { cn } from "@/utils/cn";
import { useState, useRef, useEffect, useCallback } from "react";

export interface DynamicPopoverProps {
  className?: string;
  trigger: React.ReactNode;
  triggerClassName?: string;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "end" | "center";
  sideOffset?: number;
  alignOffset?: number;
  mode?: "click" | "hover";
  triggerHidden?: boolean;
  delay?: number;
  disabled?: boolean;
  closeOnScroll?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function DynamicPopover({
  className,
  triggerClassName,
  trigger,
  children,
  side = "top",
  align = "start",
  sideOffset = 8,
  alignOffset = 0,
  mode = "click",
  triggerHidden = true,
  delay = 200,
  disabled = false, // 默认不禁用
  closeOnScroll = false,
  onOpenChange,
}: DynamicPopoverProps) {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setHidden(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleHide = () => {
    if (mode === "hover") {
      clearHideTimeout();
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, delay);
    }
  };

  const handleTriggerMouseEnter = () => {
    if (disabled) return; // 禁用时不响应
    if (mode === "hover") {
      clearHideTimeout();
      setOpen(true);
    }
  };

  const handleTriggerMouseLeave = () => {
    if (mode === "hover") {
      scheduleHide();
    }
  };

  const handleContentMouseEnter = () => {
    if (mode === "hover") {
      clearHideTimeout();
    }
  };

  const handleContentMouseLeave = () => {
    if (mode === "hover") {
      scheduleHide();
    }
  };

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (disabled && nextOpen) return; // 禁用时阻止打开
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [disabled, onOpenChange]
  );

  /**
   * 1) Popover 嵌在 Radix Dialog 内时，Dialog 的 DismissableLayerBranch 会让 Radix 自带的「外部点击关闭」失效。
   * 2) 在 document 捕获阶段补充关闭；若点击落在「不包含本 Popover 触发器」的 role=dialog 上，说明是 portaled 的嵌套对话框（如 DeleteConfirmModal），
   *    不关闭菜单，避免子树卸载打断其中的 loading/onConfirm。
   */
  useEffect(() => {
    if (!open || disabled || mode !== "click") return;

    const onPointerDownCapture = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (triggerRootRef.current?.contains(target)) return;
      if (contentRef.current?.contains(target)) return;

      const el = target instanceof Element ? target : target.parentElement;
      if (!el) {
        handleOpenChange(false);
        return;
      }

      const triggerEl = triggerRootRef.current;
      const nearestDialog = el.closest('[role="dialog"]');
      if (nearestDialog && triggerEl && !nearestDialog.contains(triggerEl)) {
        return;
      }

      handleOpenChange(false);
    };

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => document.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [open, disabled, mode, handleOpenChange]);

  useEffect(() => {
    if (!open || disabled || !closeOnScroll) return;

    const onScroll = () => {
      handleOpenChange(false);
    };

    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [open, disabled, closeOnScroll, handleOpenChange]);

  const triggerProps =
    mode === "hover"
      ? {
        onMouseEnter: handleTriggerMouseEnter,
        onMouseLeave: handleTriggerMouseLeave,
      }
      : {};

  const contentProps =
    mode === "hover"
      ? {
        onMouseEnter: handleContentMouseEnter,
        onMouseLeave: handleContentMouseLeave,
      }
      : {};

  return (
    <Popover.Root open={disabled ? false : open} onOpenChange={handleOpenChange}>
      <div
        ref={triggerRootRef}
        {...triggerProps}
        className={cn(
          "w-fit",
          disabled && "cursor-not-allowed opacity-50", // 禁用样式
          triggerClassName
        )}
      >
        <Popover.Trigger
          asChild
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            if (hidden) {
              e.preventDefault();
              e.stopPropagation();
              setHidden(false);
            }
          }}
        >
          {trigger}
        </Popover.Trigger>
      </div>

      {!disabled && (
        <Popover.Portal>
          <Popover.Content
            ref={contentRef}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            side={side}
            align={align}
            onOpenAutoFocus={(e) => e.preventDefault()}
            {...contentProps}
            className={cn(
              "z-50 w-56 rounded-sm border bg-popover p-1 text-popover-foreground shadow-md outline-none",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2",
              "data-[side=left]:slide-in-from-right-2",
              "data-[side=right]:slide-in-from-left-2",
              "data-[side=top]:slide-in-from-bottom-2",
              hidden && "invisible",
              className
            )}
            onClickCapture={() => triggerHidden && setHidden(true)}
          >
            {children}
          </Popover.Content>
        </Popover.Portal>
      )}
    </Popover.Root>
  );
}